import { useState, useEffect, useMemo } from 'react';
import { api, PO_NUMBER_MAX_LENGTH } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useBasket } from '../hooks/useBasket';
import { getCachedCatalog, setCachedCatalog } from '../lib/catalogCache';
import ProductImageModal from '../components/ProductImageModal';
import BasketIcon from '../components/BasketIcon';

// Normalize category strings: trim spaces around colons for consistent comparison
function normalizeCategory(cat) {
  return (cat || 'Uncategorized').split(':').map(s => s.trim()).filter(Boolean).join(':');
}

// ── Date helpers ─────────────────────────────────────────────────────────────
function getNextBusinessDay(date) {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d;
}

function getPickupDate() {
  const now = new Date();
  // Convert to Mountain Time for 1:30 PM cutoff
  const mt = new Date(now.toLocaleString('en-US', { timeZone: 'America/Denver' }));
  const cutoffHour = 13, cutoffMin = 30;
  const isPastCutoff = mt.getHours() > cutoffHour || (mt.getHours() === cutoffHour && mt.getMinutes() >= cutoffMin);

  let d = new Date(mt.getFullYear(), mt.getMonth(), mt.getDate());
  // If weekend or past cutoff, advance
  if (isPastCutoff || d.getDay() === 0 || d.getDay() === 6) {
    d = getNextBusinessDay(d);
  }
  return { date: d, isPastCutoff };
}

function computeDeliveryDate(method, deliveryInfo, customerData) {
  if (method === 'pickup') {
    return getPickupDate().date;
  }
  if (method === 'fedex') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let d = getNextBusinessDay(today);
    // Non-CO addresses get an extra business day
    const state = customerData?.shipping?.stateProvince?.toUpperCase();
    if (state && state !== 'CO') d = getNextBusinessDay(d);
    return d;
  }
  if (method === 'delivery' && deliveryInfo?.nextDelivery) {
    return new Date(deliveryInfo.nextDelivery);
  }
  return null;
}

function formatDateForInput(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function OrderGuide() {
  const { user } = useAuth();
  const { addItems } = useBasket();
  const canPurchaseDirectly = !user?.role || user?.role === 'customer_admin' || user?.permissions?.canPurchaseDirectly;
  const [addedToBasket, setAddedToBasket] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [orderHints, setOrderHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');
  const [quantities, setQuantities] = useState({});
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [requestedDate, setRequestedDate] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [browseMode, setBrowseMode] = useState('type'); // 'type' | 'brand'
  const [zoomProduct, setZoomProduct] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [customerData, setCustomerData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        // 1. Try client cache first for catalog
        let catalogData = getCachedCatalog();
        if (!catalogData) {
          catalogData = await api.getCatalog();
          setCachedCatalog(catalogData);
        }

        // 2. Fetch order history hints
        const guideData = await api.getOrderGuide();

        if (cancelled) return;

        // 3. Build order hints map keyed by SOS item ID
        const hints = {};
        (guideData.guide || []).forEach(g => {
          const hint = {
            orderCount: g.orderCount,
            lastOrdered: g.lastOrdered,
            lastQty: g.lastQty,
          };
          if (g.itemId) hints[String(g.itemId)] = hint;
        });

        // 4. Merge hints into catalog — match by sosId (SOS internal ID), id, or sku
        const products = (catalogData.products || []).map(p => {
          const hint = (p.sosId && hints[String(p.sosId)]) || hints[String(p.id)] || hints[String(p.sku)] || {};
          return {
            ...p,
            ...hint,
            suggestedQty: hint.lastQty || 0,
          };
        });

        setCatalog(products);
        setOrderHints(hints);

        // 5. No pre-filled quantities — cart starts empty
        setQuantities({});
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, []);

  // Fetch customer + delivery info
  useEffect(() => {
    let cancelled = false;
    async function loadCustomerData() {
      try {
        const [cust, del] = await Promise.all([api.getCustomer(), api.getDelivery()]);
        if (cancelled) return;
        setCustomerData(cust);
        setDeliveryInfo(del);
        // Set initial delivery method from SOS data
        if (del.shippingMethod === 'fedex') setDeliveryMethod('fedex');
        else setDeliveryMethod('delivery');
        // Pre-fill date from delivery info
        const initDate = computeDeliveryDate(
          del.shippingMethod === 'fedex' ? 'fedex' : 'delivery',
          del, cust
        );
        if (initDate) setRequestedDate(formatDateForInput(initDate));
      } catch (e) {
        console.warn('Could not load customer/delivery data:', e.message);
      }
    }
    loadCustomerData();
    return () => { cancelled = true; };
  }, []);

  // Lock body scroll when checkout panel is open
  useEffect(() => {
    document.body.style.overflow = checkoutOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [checkoutOpen]);

  const setQty = (id, val) => {
    // Prevent quantity changes on special order items
    const item = catalog.find(p => String(p.id) === String(id));
    if (item?.specialOrder) return;
    const n = Math.max(0, parseInt(val) || 0);
    setQuantities(q => ({ ...q, [id]: n }));
  };

  // Helper: check if a product has order history
  function hasOrderHistory(p) {
    return !!(
      (p.sosId && orderHints[String(p.sosId)]) ||
      orderHints[String(p.id)] ||
      orderHints[String(p.sku)]
    );
  }

  // All products (before category/search filters)
  const tabProducts = useMemo(() => catalog, [catalog]);

  // Build the sidebar tree based on the current browse mode.
  // - 'type'  → Category → Subcategory
  // - 'brand' → Brand → Category → Subcategory
  // Filter paths are colon-delimited and read by matchesFilter() below; the
  // segment order matches the tree's nesting order for the current mode.
  const categoryTree = useMemo(() => {
    const tree = {};

    function ensure(node, key) {
      if (!node[key]) node[key] = { _count: 0, _children: {} };
      return node[key];
    }

    tabProducts.forEach(p => {
      const cat = (p.category || 'Uncategorized').trim();
      const brand = (p.brand || '').trim();
      const sub = (p.subCategory || '').trim();

      if (browseMode === 'brand') {
        // Brand → Category → Subcategory. Products without a brand are
        // grouped under "(No Brand)" so they remain reachable in this mode.
        const brandKey = brand || '(No Brand)';
        const brandNode = ensure(tree, brandKey);
        brandNode._count++;
        const catNode = ensure(brandNode._children, cat);
        catNode._count++;
        if (sub) {
          ensure(catNode._children, sub)._count++;
        }
      } else {
        // 'type' → Category → Subcategory.
        const catNode = ensure(tree, cat);
        catNode._count++;
        if (sub) {
          ensure(catNode._children, sub)._count++;
        }
      }
    });

    function toSortedArray(obj) {
      return Object.entries(obj)
        .map(([name, data]) => ({
          name,
          count: data._count,
          children: toSortedArray(data._children),
        }))
        .sort((a, b) => {
          // Push "(No Brand)" / "Uncategorized" buckets to the bottom so the
          // real names sort alphabetically at the top of each level.
          const aLast = a.name === '(No Brand)' || a.name === 'Uncategorized';
          const bLast = b.name === '(No Brand)' || b.name === 'Uncategorized';
          if (aLast !== bLast) return aLast ? 1 : -1;
          return a.name.localeCompare(b.name);
        });
    }

    return toSortedArray(tree);
  }, [tabProducts, browseMode]);

  // Total product count for current tab
  const viewTotal = useMemo(() => tabProducts.length, [tabProducts]);

  // Helper: check if a product matches the active filter.
  // Filter format is colon-delimited; the segment order depends on browseMode:
  //   'type'  → "Category" or "Category:Subcategory"
  //   'brand' → "Brand" or "Brand:Category" or "Brand:Category:Subcategory"
  // Products without a brand are bucketed under "(No Brand)" in brand mode.
  function matchesFilter(p, filter) {
    const filterParts = filter.split(':');
    const cat = (p.category || 'Uncategorized').trim();
    const brand = (p.brand || '').trim();
    const sub = (p.subCategory || '').trim();

    if (browseMode === 'brand') {
      const brandKey = brand || '(No Brand)';
      if (brandKey !== filterParts[0]) return false;
      if (filterParts.length > 1 && cat !== filterParts[1]) return false;
      if (filterParts.length > 2 && sub !== filterParts[2]) return false;
      return true;
    }

    // 'type' mode
    if (cat !== filterParts[0]) return false;
    if (filterParts.length > 1 && sub !== filterParts[1]) return false;
    return true;
  }

  // Auto-reset activeFilter if it no longer matches any products in current tab
  useEffect(() => {
    if (activeFilter !== 'All') {
      const hasMatch = tabProducts.some(p => matchesFilter(p, activeFilter));
      if (!hasMatch) {
        setActiveFilter('All');
        setExpandedNodes(new Set());
      }
    }
  }, [tabProducts, activeFilter]);

  // Filter products by activeFilter and search.
  // Search is universal — when the user types anything, we ignore the
  // sidebar selection and search the full catalog. (Otherwise picking a
  // brand on the left silently scopes the search box, which surprised
  // customers who expected "search" to mean "search everything".)
  const filteredProducts = useMemo(() => {
    if (search) {
      const q = search.toLowerCase();
      return tabProducts.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    }

    if (activeFilter !== 'All') {
      return tabProducts.filter(p => matchesFilter(p, activeFilter));
    }

    return tabProducts;
  }, [tabProducts, activeFilter, search]);

  const orderLines = Object.entries(quantities)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const item = catalog.find(g => String(g.id) === String(id));
      // Use sosId (SOS internal ID) for the order — fall back to catalog id
      const sosItemId = item?.sosId || id;
      return { itemId: sosItemId, quantity: qty, price: item?.price, description: item?.name, hasSosId: !!item?.sosId };
    });

  const orderTotal = orderLines.reduce((sum, l) => {
    return sum + (l.price || 0) * l.quantity;
  }, 0);

  function addToBasket() {
    if (orderLines.length === 0) return;

    // Warn if any items don't have an SOS mapping
    const unmapped = orderLines.filter(l => !l.hasSosId);
    if (unmapped.length > 0) {
      const names = unmapped.map(l => l.description).join(', ');
      setError(`These products are not yet available for ordering: ${names}. Please contact Silver Service to add them.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const basketItems = orderLines.map(l => {
      const item = catalog.find(g =>
        (g.sosId && String(g.sosId) === String(l.itemId)) ||
        String(g.id) === String(l.itemId)
      );
      return {
        itemId: item?.id || l.itemId,
        sosId: item?.sosId || l.itemId,
        name: item?.name || l.description,
        // Catalog "description" is the longer SOS-master detail line
        // (e.g. "150 pods per case") — Silver Service warehouse needs
        // it to pick/pack the order, so capture it for the SOS line.
        description: item?.description || '',
        sku: item?.sku || '',
        quantity: l.quantity,
        price: l.price || 0,
        onSale: !!item?.onSale,
        originalPrice: item?.originalPrice ?? item?.price ?? 0,
        saleLabel: item?.saleLabel || null,
      };
    });

    addItems(basketItems);
    setAddedToBasket(true);
    setQuantities({});
    setCheckoutOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setAddedToBasket(false), 5000);
  }

  function handleNodeClick(fullPath, hasChildren) {
    if (activeFilter === fullPath) {
      // Already selected — just toggle expand/collapse
      if (hasChildren) {
        setExpandedNodes(prev => {
          const next = new Set(prev);
          if (next.has(fullPath)) next.delete(fullPath);
          else next.add(fullPath);
          return next;
        });
      }
    } else {
      // New selection — select and expand
      setActiveFilter(fullPath);
      if (hasChildren) {
        setExpandedNodes(prev => {
          const next = new Set(prev);
          next.add(fullPath);
          return next;
        });
      }
    }
  }

  function renderTreeNode(node, parentPath, depth) {
    const fullPath = parentPath ? `${parentPath}:${node.name}` : node.name;
    const isExpanded = expandedNodes.has(fullPath);
    const isActive = activeFilter === fullPath;
    const hasChildren = node.children.length > 0;

    return (
      <div key={fullPath}>
        <button
          style={sidebarNodeBtn(isActive, depth)}
          onClick={() => handleNodeClick(fullPath, hasChildren)}
        >
          <span style={styles.catNameRow}>
            {hasChildren && (
              <span style={{
                ...styles.expandArrow,
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}>›</span>
            )}
            {node.name}
          </span>
          <span style={styles.catCount}>{node.count}</span>
        </button>
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderTreeNode(child, fullPath, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  if (loading) return <div style={styles.loading}>Loading products...</div>;

  return (
    <div style={styles.root}>
      {/* Header */}
      <div data-page-header style={styles.pageHeader}>
        <div style={{flex:1}}>
          <div style={styles.headerTop}>
            <h1 style={styles.title}>Product Catalog</h1>
            <button
              style={styles.requestProductBtn}
              onClick={() => { window.location.hash = 'request'; }}
            >
              <span style={{ fontSize: '1rem' }}>🔍</span>
              Can't find a product?
            </button>
          </div>
          <p style={styles.subtitle}>
            {`${catalog.length} products · Browse and add items to your basket`}
          </p>
        </div>
      </div>

      {addedToBasket && (
        <div style={styles.successBanner}>
          Items added to basket. <a href="#basket" style={{color:'#27ae60', fontWeight:600}}>View Basket</a>
          <button style={styles.dismissBtn} onClick={() => setAddedToBasket(false)}>✕</button>
        </div>
      )}
      {submitted && (
        <div style={styles.successBanner}>
          ✓ {submitted.message} — Your order is in SOS Inventory.
          <button style={styles.dismissBtn} onClick={() => setSubmitted(null)}>✕</button>
        </div>
      )}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Two-column layout: sidebar + main content */}
      <div data-content-layout style={styles.contentLayout}>
        {/* Category sidebar */}
        <div data-sidebar style={styles.sidebar}>
          <div style={styles.sidebarTitle}>Browse</div>

          {/* All Products button */}
          <button
            style={sidebarNodeBtn(activeFilter === 'All', 0)}
            onClick={() => { setActiveFilter('All'); setExpandedNodes(new Set()); }}
          >
            <span>All Products</span>
            <span style={styles.catCount}>{viewTotal}</span>
          </button>

          {/* Browse-mode toggle */}
          <div style={styles.modeToggle}>
            <button
              style={modeToggleBtn(browseMode === 'type')}
              onClick={() => {
                if (browseMode === 'type') return;
                setBrowseMode('type');
                setActiveFilter('All');
                setExpandedNodes(new Set());
              }}
            >
              By Product Type
            </button>
            <button
              style={modeToggleBtn(browseMode === 'brand')}
              onClick={() => {
                if (browseMode === 'brand') return;
                setBrowseMode('brand');
                setActiveFilter('All');
                setExpandedNodes(new Set());
              }}
            >
              By Brand
            </button>
          </div>

          {/* Category tree */}
          {categoryTree.map(node => renderTreeNode(node, '', 0))}
        </div>

        {/* Main content */}
        <div style={styles.mainContent}>
          {/* Search */}
          <div style={styles.searchRow}>
            <input
              style={styles.search}
              placeholder="Search by name, SKU, brand, or product code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span style={styles.resultCount}>{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Product table */}
          <div data-product-table style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={th()}>Product</th>
                  <th style={th('center')}>Last Ordered</th>
                  <th style={th('right')}>Price</th>
                  <th style={th('center')}>Qty</th>
                  <th style={th('right')}>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 && (
                  <tr><td colSpan={5} style={{padding:'2rem',textAlign:'center',color:'rgba(0,0,0,0.3)'}}>No products found</td></tr>
                )}
                {filteredProducts.map((item, idx) => {
                  const qty = quantities[item.id] || 0;
                  const lineTotal = (item.price || 0) * qty;
                  const isSpecialOrder = !!item.specialOrder;
                  return (
                    <tr key={`${item.id}-${idx}`} data-product-row style={{ ...styles.row, ...(isSpecialOrder ? styles.specialOrderRow : {}) }}>
                      <td data-cell="product" style={styles.tdProduct}>
                        <div style={styles.productRow}>
                          {item.hasImage && item.sku && (
                            <img
                              data-product-thumb
                              src={`/images/products/${item.sku}.jpg`}
                              alt=""
                              loading="lazy"
                              style={styles.thumbnail}
                              onClick={() => setZoomProduct(item)}
                              onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                          )}
                          <div style={styles.productText}>
                            <div style={{ ...styles.productName, ...(isSpecialOrder ? { color: 'rgba(0,0,0,0.4)' } : {}) }}>{item.name}</div>
                            <div style={styles.productMeta}>
                              {item.sku && <span style={styles.sku}>{item.sku}</span>}
                              {item.brand && <span style={{ ...styles.brand, ...(isSpecialOrder ? styles.specialOrderBrand : {}) }}>{item.brand}</span>}
                              {item.format && <span style={styles.tag}>{item.format}</span>}
                              {isSpecialOrder && <span style={styles.specialOrderBadge}>Special Order</span>}
                              {!isSpecialOrder && item.orderCount > 0 && <span style={styles.orderBadge}>Ordered {item.orderCount}x</span>}
                              {!isSpecialOrder && item.onSale && <span style={styles.saleBadge}>{item.saleLabel || 'SALE'}</span>}
                            </div>
                            {item.description && (
                              <div style={styles.description}>{item.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td data-cell="last-ordered" style={{...td('center'), fontSize:'0.8rem', color:'rgba(0,0,0,0.4)'}}>
                        {item.lastOrdered
                          ? new Date(item.lastOrdered).toLocaleDateString('en-US', {month:'short', day:'numeric'})
                          : '—'}
                      </td>
                      <td data-cell="price" style={{...td('right'), fontFamily:'monospace', color: isSpecialOrder ? 'rgba(0,0,0,0.3)' : '#A50B18'}}>
                        {!isSpecialOrder && item.onSale && item.originalPrice > item.price ? (
                          <>
                            <span style={{ textDecoration:'line-through', color:'rgba(0,0,0,0.35)', fontSize:'0.8em' }}>${item.originalPrice.toFixed(2)}</span>
                            <div>${item.price.toFixed(2)}</div>
                          </>
                        ) : (
                          item.price ? `$${item.price.toFixed(2)}` : '—'
                        )}
                      </td>
                      <td data-cell="qty" style={td('center')}>
                        {isSpecialOrder ? (
                          <div style={styles.specialOrderContact}>
                            <button
                              type="button"
                              style={styles.specialOrderLink}
                              onClick={() => {
                                // Stash item details so the request form can
                                // pre-populate. ProductRequest.jsx reads this
                                // on mount and clears the key after applying.
                                try {
                                  sessionStorage.setItem('productRequestPrefill', JSON.stringify({
                                    productDescription: item.name || '',
                                    brand: item.brand || '',
                                    additionalNotes: [
                                      'Special order item from the catalog.',
                                      item.sku ? `SKU: ${item.sku}` : null,
                                      item.format ? `Format: ${item.format}` : null,
                                    ].filter(Boolean).join(' · '),
                                    sourceLabel: item.name,
                                  }));
                                } catch (_) { /* sessionStorage disabled — proceed anyway */ }
                                window.location.hash = 'request';
                              }}
                            >
                              Request to order
                            </button>
                          </div>
                        ) : (
                          <div style={styles.qtyControl}>
                            <button style={styles.qtyBtn} onClick={() => setQty(item.id, qty - 1)}>−</button>
                            <input
                              style={styles.qtyInput}
                              type="number"
                          inputMode="numeric"
                              min="0"
                              value={qty || ''}
                              placeholder="0"
                              onChange={e => setQty(item.id, e.target.value)}
                            />
                            <button style={styles.qtyBtn} onClick={() => setQty(item.id, qty + 1)}>+</button>
                          </div>
                        )}
                      </td>
                      <td data-cell="line-total" style={{...td('right'), fontFamily:'monospace', color: isSpecialOrder ? 'rgba(0,0,0,0.2)' : qty > 0 ? '#333' : 'rgba(0,0,0,0.2)'}}>
                        {isSpecialOrder ? '—' : qty > 0 ? `$${lineTotal.toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pending "Add to Basket" bar — floats and sticks to the bottom of the
          viewport on desktop so it stays in reach while scrolling a long
          catalog; becomes a fixed footer on mobile (see [data-pending-basket-bar]
          in index.html). Mirrors the Order Guide. */}
      {orderLines.length > 0 && (
        <div data-pending-basket-bar style={styles.basketPanel}>
          <div style={styles.basketSummary}>
            <span style={styles.basketSummaryText}>
              <BasketIcon />
              {orderLines.length} item{orderLines.length !== 1 ? 's' : ''} ready &middot; <strong style={styles.basketTotal}>${orderTotal.toFixed(2)}</strong>
            </span>
            <div style={styles.basketActions}>
              <button data-pending-clear style={styles.clearBtn} onClick={() => setQuantities({})}>Clear All</button>
              <button data-add-basket style={styles.addToBasketBtn} onClick={addToBasket}>
                <BasketIcon light />
                Add to Basket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout overlay + side panel */}
      {checkoutOpen && orderLines.length > 0 && (
        <>
          <div style={styles.overlay} onClick={() => setCheckoutOpen(false)} />
          <div data-checkout-panel style={styles.checkoutPanel}>
            <div style={styles.checkoutHeader}>
              <h3 style={styles.checkoutTitle}>Checkout</h3>
              <button style={styles.checkoutClose} onClick={() => setCheckoutOpen(false)}>×</button>
            </div>

            <div style={styles.checkoutBody}>
              {/* Shipping Address */}
              <div style={styles.checkoutSection}>
                <div style={styles.sectionLabel}>Shipping Address</div>
                {customerData?.shipping?.line1 ? (
                  <div style={styles.addressBlock}>
                    <div>{customerData.shipping.line1}</div>
                    {customerData.shipping.line2 && <div>{customerData.shipping.line2}</div>}
                    <div>{customerData.shipping.city}, {customerData.shipping.stateProvince} {customerData.shipping.postalCode}</div>
                  </div>
                ) : (
                  <div style={styles.mutedText}>No address on file</div>
                )}
              </div>

              {/* Delivery Method */}
              <div style={styles.checkoutSection}>
                <div style={styles.sectionLabel}>Delivery Method</div>
                <div style={styles.methodCards}>
                  <button
                    style={methodCard(deliveryMethod === 'delivery')}
                    onClick={() => {
                      setDeliveryMethod('delivery');
                      const d = computeDeliveryDate('delivery', deliveryInfo, customerData);
                      if (d) setRequestedDate(formatDateForInput(d));
                    }}
                  >
                    <div style={styles.methodName}>Silver Service Delivery</div>
                    <div style={styles.methodDetail}>
                      {deliveryInfo?.deliveryDay
                        ? `Next delivery: ${deliveryInfo.deliveryDay}`
                        : 'Contact us for scheduling'}
                    </div>
                  </button>
                  <button
                    style={methodCard(deliveryMethod === 'fedex')}
                    onClick={() => {
                      setDeliveryMethod('fedex');
                      const d = computeDeliveryDate('fedex', deliveryInfo, customerData);
                      if (d) setRequestedDate(formatDateForInput(d));
                    }}
                  >
                    <div style={styles.methodName}>FedEx</div>
                    <div style={styles.methodDetail}>
                      {customerData?.shipping?.stateProvince?.toUpperCase() === 'CO'
                        ? 'Next business day'
                        : '2–3 business days'}
                    </div>
                  </button>
                  <button
                    style={methodCard(deliveryMethod === 'pickup')}
                    onClick={() => {
                      setDeliveryMethod('pickup');
                      const d = computeDeliveryDate('pickup', deliveryInfo, customerData);
                      if (d) setRequestedDate(formatDateForInput(d));
                    }}
                  >
                    <div style={styles.methodName}>Pick Up</div>
                    <div style={styles.methodDetail}>
                      {(() => {
                        const { isPastCutoff } = getPickupDate();
                        return isPastCutoff
                          ? 'Ready next business day'
                          : 'Ready today (order before 1:30 PM MST)';
                      })()}
                    </div>
                  </button>
                </div>
              </div>

              {/* Date */}
              <div style={styles.checkoutSection}>
                <div style={styles.sectionLabel}>
                  {deliveryMethod === 'pickup' ? 'Pickup Date' : 'Delivery Date'}
                </div>
                <input
                  style={styles.input}
                  type="date"
                  value={requestedDate}
                  onChange={e => setRequestedDate(e.target.value)}
                />
                {deliveryMethod === 'pickup' && getPickupDate().isPastCutoff && (
                  <div style={styles.cutoffWarning}>
                    Orders placed after 1:30 PM MST will be ready the next business day.
                  </div>
                )}
              </div>

              {/* PO + Notes */}
              <div style={styles.checkoutSection}>
                <div style={styles.field}>
                  <label style={styles.label}>Purchase Order Number / Reference / Email Address *</label>
                  <input style={styles.input} placeholder="PO number, reference, or your email address" value={poNumber} onChange={e => setPoNumber(e.target.value)} maxLength={PO_NUMBER_MAX_LENGTH} required />
                </div>
                <div style={{...styles.field, marginTop: '0.75rem'}}>
                  <label style={styles.label}>Notes</label>
                  <textarea style={{...styles.input, resize:'vertical', minHeight:60}} placeholder="Any special instructions..." value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
              </div>

              {/* Order Summary */}
              <div style={styles.checkoutSection}>
                <div style={styles.sectionLabel}>Order Summary</div>
                <div style={styles.summaryLines}>
                  {orderLines.map(l => {
                    const item = catalog.find(g =>
                      (g.sosId && String(g.sosId) === String(l.itemId)) ||
                      String(g.id) === String(l.itemId)
                    );
                    const catalogId = item?.id || l.itemId;
                    return (
                      <div key={l.itemId} style={styles.summaryLine}>
                        <span style={styles.summaryLineName}>{item?.name || l.itemId}</span>
                        <span style={styles.summaryLineRight}>
                          <span style={{fontFamily:'monospace'}}>×{l.quantity} = ${((item?.price||0)*l.quantity).toFixed(2)}</span>
                          <button
                            style={styles.removeItemBtn}
                            title="Remove item"
                            onClick={() => setQty(catalogId, 0)}
                          >×</button>
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={styles.summaryTotal}>
                  <span>Estimated Total</span>
                  <span style={{color:'#A50B18',fontWeight:700}}>${orderTotal.toFixed(2)}</span>
                </div>
                <p style={styles.summaryNote}>Final total may vary based on tax and any adjustments by Silver Service.</p>
              </div>
            </div>

            {/* Footer actions */}
            <div style={styles.checkoutFooter}>
              <button
                style={styles.clearCartBtn}
                onClick={() => { setQuantities({}); setError(''); setCheckoutOpen(false); }}
              >
                Clear Cart
              </button>
              <button style={styles.submitBtn} onClick={submitOrder} disabled={submitting}>
                {submitting
                  ? (canPurchaseDirectly ? 'Placing Order...' : 'Submitting...')
                  : canPurchaseDirectly
                    ? `Place Order — ${orderLines.length} item${orderLines.length !== 1 ? 's' : ''}`
                    : `Submit for Approval — ${orderLines.length} item${orderLines.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </>
      )}

      <ProductImageModal product={zoomProduct} onClose={() => setZoomProduct(null)} />
    </div>
  );
}

const th = (align = 'left') => ({
  padding: '0.75rem 1rem', textAlign: align,
  fontSize: '0.72rem', fontWeight: 600,
  color: 'rgba(0,0,0,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase',
  borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap',
});
const td = (align = 'left') => ({
  padding: '0.85rem 1rem', textAlign: align, verticalAlign: 'middle',
});

const sidebarNodeBtn = (active, depth = 0) => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  width: '100%',
  padding: `0.5rem 0.75rem 0.5rem ${0.75 + depth * 0.9}rem`,
  border: 'none', borderRadius: 0,
  borderLeft: active ? '3px solid #A50B18' : '3px solid transparent',
  background: active ? 'rgba(165,11,24,0.06)' : 'transparent',
  color: active ? '#A50B18' : '#333',
  fontSize: `${Math.max(0.78, 0.85 - depth * 0.025)}rem`,
  fontWeight: active ? 600 : 400,
  cursor: 'pointer', textAlign: 'left',
  transition: 'all 0.15s',
});

const modeToggleBtn = (active) => ({
  flex: 1,
  padding: '0.4rem 0.5rem',
  border: '1px solid',
  borderColor: active ? '#A50B18' : '#ddd',
  borderRadius: 4,
  background: active ? '#A50B18' : '#fff',
  color: active ? '#fff' : 'rgba(0,0,0,0.55)',
  fontSize: '0.75rem',
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  transition: 'all 0.15s',
  fontFamily: 'inherit',
});

const tabBtn = (active) => ({
  padding: '0.6rem 1.2rem', border: 'none', borderRadius: 0,
  background: 'transparent',
  color: active ? '#A50B18' : 'rgba(0,0,0,0.45)',
  fontSize: '0.9rem', fontWeight: active ? 600 : 400,
  cursor: 'pointer',
  borderBottom: active ? '2px solid #A50B18' : '2px solid transparent',
  transition: 'all 0.15s',
});

const methodCard = (active) => ({
  display: 'block', width: '100%', textAlign: 'left',
  padding: '0.75rem 1rem', borderRadius: 8,
  border: active ? '2px solid #A50B18' : '1px solid #ddd',
  background: active ? 'rgba(165,11,24,0.04)' : '#fff',
  cursor: 'pointer', transition: 'all 0.15s',
});

const styles = {
  root: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  loading: { color: 'rgba(0,0,0,0.4)', padding: '3rem', textAlign: 'center' },
  pageHeader: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' },
  requestProductBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    background: '#fff', color: '#A50B18', border: '1px solid #A50B18',
    borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  title: { fontSize: '1.5rem', fontWeight: 700, color: '#333', margin: 0 },
  tabBar: { display: 'flex', gap: 0, borderBottom: '1px solid #e0e0e0', marginTop: '0.5rem' },
  subtitle: { fontSize: '0.82rem', color: 'rgba(0,0,0,0.4)', margin: '0.5rem 0 0' },
  // Pending "Add to Basket" bar — sticks to the bottom of the viewport on
  // desktop; index.html turns it into a fixed footer on mobile. Shared look
  // with the Order Guide page.
  basketPanel: {
    position: 'sticky', bottom: '1.25rem', zIndex: 50,
    background: '#fff', border: '1px solid #A50B18', borderRadius: 12,
    padding: '0.9rem 1.5rem',
    boxShadow: '0 10px 30px rgba(165,11,24,0.20), 0 2px 8px rgba(0,0,0,0.10)',
  },
  basketSummary: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' },
  basketSummaryText: { display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '1.05rem', fontWeight: 600, color: '#1a1a1a' },
  basketTotal: { fontWeight: 800, color: '#A50B18' },
  basketActions: { display: 'flex', gap: '0.85rem', alignItems: 'center' },
  clearBtn: { background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '0.55rem 0.9rem', fontSize: '0.82rem', color: 'rgba(0,0,0,0.45)', cursor: 'pointer' },
  addToBasketBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    background: '#A50B18', color: '#fff', border: 'none', borderRadius: 8,
    padding: '0.8rem 1.75rem', fontSize: '1rem', fontWeight: 800, cursor: 'pointer',
    letterSpacing: '0.02em', boxShadow: '0 3px 10px rgba(165,11,24,0.35)',
  },
  successBanner: { background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.25)', borderRadius: 8, padding: '1rem 1.25rem', color: '#27ae60', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dismissBtn: { background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1rem' },
  errorBanner: { background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, padding: '1rem 1.25rem', color: '#c0392b' },

  // Two-column layout
  contentLayout: { display: 'flex', gap: '1rem', alignItems: 'flex-start' },
  sidebar: {
    width: 240, minWidth: 240, flexShrink: 0,
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
    position: 'sticky', top: '1rem',
    maxHeight: 'calc(100vh - 10rem)', overflowY: 'auto',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  sidebarTitle: {
    padding: '0.75rem 0.75rem 0.5rem',
    fontSize: '0.72rem', fontWeight: 600,
    color: 'rgba(0,0,0,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase',
    borderBottom: '1px solid #f0f0f0',
  },
  modeToggle: {
    display: 'flex', gap: '0.35rem',
    padding: '0.5rem 0.75rem 0.6rem',
    borderBottom: '1px solid #f0f0f0',
  },
  catNameRow: { display: 'flex', alignItems: 'center', gap: '0.3rem' },
  expandArrow: {
    fontSize: '1rem', lineHeight: 1, color: 'rgba(0,0,0,0.3)',
    transition: 'transform 0.2s', display: 'inline-block',
  },
  catCount: {
    fontSize: '0.72rem', fontWeight: 600,
    color: 'rgba(0,0,0,0.35)', background: '#f0f0f0',
    padding: '0.1rem 0.45rem', borderRadius: 10, minWidth: 22, textAlign: 'center',
    flexShrink: 0,
  },
  mainContent: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1rem' },

  searchRow: { display: 'flex', gap: '1rem', alignItems: 'center' },
  description: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.4, marginTop: '0.35rem', maxWidth: 560 },
  productRow: { display: 'flex', gap: '0.85rem', alignItems: 'flex-start' },
  productText: { flex: 1, minWidth: 0 },
  thumbnail: { width: 140, height: 140, objectFit: 'contain', flexShrink: 0, background: '#f7f5f2', borderRadius: 6, border: '1px solid #eee', cursor: 'zoom-in' },
  search: { flex: 1, background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '0.7rem 1rem', color: '#333', fontSize: '0.9rem', outline: 'none' },
  resultCount: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.4)', whiteSpace: 'nowrap' },
  tableWrap: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  row: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.1s' },
  tdProduct: { padding: '0.85rem 1rem', minWidth: 460 },
  productName: { fontSize: '0.92rem', fontWeight: 500, color: '#333', marginBottom: 3 },
  productMeta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  sku: { fontSize: '0.72rem', color: 'rgba(0,0,0,0.35)', fontFamily: 'monospace' },
  brand: { fontSize: '0.72rem', color: '#A50B18', background: 'rgba(165,11,24,0.08)', padding: '0.1rem 0.4rem', borderRadius: 3 },
  tag: { fontSize: '0.72rem', color: 'rgba(0,0,0,0.45)', background: '#f0f0f0', padding: '0.1rem 0.4rem', borderRadius: 3 },
  orderBadge: { fontSize: '0.68rem', color: '#27ae60', background: 'rgba(39,174,96,0.08)', padding: '0.1rem 0.4rem', borderRadius: 3, fontWeight: 600 },
  saleBadge: { fontSize: '0.68rem', color: '#fff', background: '#A50B18', padding: '0.1rem 0.4rem', borderRadius: 3, fontWeight: 700, letterSpacing: '0.03em' },
  specialOrderRow: { background: 'rgba(0,0,0,0.02)', opacity: 0.7 },
  specialOrderBadge: { fontSize: '0.68rem', color: '#888', background: 'rgba(0,0,0,0.06)', padding: '0.1rem 0.4rem', borderRadius: 3, fontWeight: 600 },
  specialOrderBrand: { color: 'rgba(0,0,0,0.35)', background: 'rgba(0,0,0,0.04)' },
  specialOrderContact: { textAlign: 'center' },
  specialOrderLink: { fontSize: '0.72rem', color: '#A50B18', textDecoration: 'underline', fontWeight: 500, whiteSpace: 'nowrap', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },
  qtyControl: { display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' },
  qtyBtn: { width: 28, height: 28, background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 4, color: '#333', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  qtyInput: { width: 56, textAlign: 'center', background: '#fff', border: '1px solid #ddd', borderRadius: 4, color: '#333', fontSize: '0.9rem', padding: '0.3rem', fontFamily: 'monospace', outline: 'none' },
  // Checkout panel
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200 },
  checkoutPanel: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '100vw', background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)' },
  checkoutHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e0e0e0', flexShrink: 0 },
  checkoutTitle: { margin: 0, color: '#A50B18', fontSize: '1.1rem', fontWeight: 700 },
  checkoutClose: { background: 'none', border: 'none', fontSize: '1.5rem', color: 'rgba(0,0,0,0.4)', cursor: 'pointer', padding: '0.25rem', lineHeight: 1 },
  checkoutBody: { flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' },
  checkoutSection: { marginBottom: '1.5rem' },
  sectionLabel: { fontSize: '0.72rem', fontWeight: 600, color: 'rgba(0,0,0,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' },
  addressBlock: { fontSize: '0.9rem', color: '#333', lineHeight: 1.6 },
  mutedText: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' },
  methodCards: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  methodName: { fontSize: '0.9rem', fontWeight: 600, color: '#333', marginBottom: 2 },
  methodDetail: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.45)' },
  cutoffWarning: { fontSize: '0.78rem', color: '#e67e22', marginTop: '0.4rem' },
  checkoutFooter: { padding: '1rem 1.5rem', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '0.75rem', alignItems: 'center', flexShrink: 0 },
  clearCartBtn: { background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: 'rgba(0,0,0,0.45)', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)', fontWeight: 500, letterSpacing: '0.04em' },
  input: { background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '0.65rem 0.9rem', color: '#333', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit' },
  orderSummary: { borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginBottom: '1rem' },
  summaryLines: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' },
  summaryLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)', gap: '0.5rem' },
  summaryLineName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  summaryLineRight: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 },
  removeItemBtn: { background: 'none', border: 'none', color: 'rgba(0,0,0,0.3)', cursor: 'pointer', fontSize: '1rem', padding: '0 0.2rem', lineHeight: 1, transition: 'color 0.15s' },
  summaryTotal: { display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 600, color: '#333', paddingTop: '0.5rem', borderTop: '1px solid #e0e0e0', marginTop: '0.5rem' },
  summaryNote: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.35)', margin: '0.5rem 0 0' },
  submitBtn: { flex: 1, background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.9rem', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.03em' },
};
